import { useEffect, useRef, useState } from "react";
import { siteConfig } from "@/lib/site-config";

interface FormEmbedProps {
  id?: string;
  className?: string;
  /**
   * When true, the LeadConnector iframe (and its heavy runtime: Google Fonts,
   * Facebook pixel, Cloudflare Turnstile, ~460KB of JS) is not loaded until the
   * first genuine user interaction. Used for the eager-looking hero form so that
   * none of that third-party weight is on the critical path. The modal form
   * loads immediately (defer=false) since opening it IS the interaction.
   */
  defer?: boolean;
}

const RESERVED_HEIGHT = 722; // measured settled height of this embed at desktop widths

const FormEmbed = ({ id = `inline-${siteConfig.widgets.formId}`, className = "", defer = false }: FormEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(!defer);

  // Interaction gate (deferred instances only).
  useEffect(() => {
    if (loaded) return;
    const events = ["pointerdown", "pointermove", "touchstart", "keydown", "wheel"];
    const onInteract = () => {
      setLoaded(true);
      events.forEach((e) => window.removeEventListener(e, onInteract));
    };
    events.forEach((e) => window.addEventListener(e, onInteract, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onInteract));
  }, [loaded]);

  // Inject form_embed.js once the form is actually loading.
  useEffect(() => {
    if (!loaded) return;
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    } else {
      try {
        // @ts-expect-error - global form embed init
        if (typeof window.formEmbedInit === "function") window.formEmbedInit();
      } catch {
        // noop
      }
    }
  }, [loaded]);

  return (
    <div ref={containerRef} className={className}>
      {/*
        Min-height guard. form_embed.js resizes the iframe several times while
        loading; reserving the settled height on a wrapper decouples that
        internal resizing from surrounding content (prevents CLS).
      */}
      <div style={{ minHeight: RESERVED_HEIGHT }}>
        {loaded ? (
          <iframe
            src={siteConfig.widgets.formIframeSrc}
            style={{ width: "100%", height: RESERVED_HEIGHT, border: "none", borderRadius: 10, display: "block" }}
            id={id}
            data-layout="{'id':'INLINE'}"
            data-trigger-type="alwaysShow"
            data-trigger-value=""
            data-activation-type="alwaysActivated"
            data-activation-value=""
            data-deactivation-type="neverDeactivate"
            data-deactivation-value=""
            data-form-name={siteConfig.widgets.formName}
            data-height={siteConfig.widgets.formHeight}
            data-layout-iframe-id={id}
            data-form-id={siteConfig.widgets.formId}
            title={siteConfig.widgets.formName}
            scrolling="no"
          />
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            aria-label="Load the free estimate form"
            className="form-facade flex h-full w-full flex-col gap-4 p-1 text-left"
            style={{ minHeight: RESERVED_HEIGHT }}
          >
            <span className="form-facade-line h-4 w-24 rounded bg-muted" />
            <span className="form-facade-line h-11 w-full rounded-lg bg-muted" />
            <span className="form-facade-line h-4 w-24 rounded bg-muted" />
            <span className="form-facade-line h-11 w-full rounded-lg bg-muted" />
            <span className="form-facade-line h-4 w-28 rounded bg-muted" />
            <span className="form-facade-line h-11 w-full rounded-lg bg-muted" />
            <span className="form-facade-line h-4 w-24 rounded bg-muted" />
            <span className="form-facade-line h-24 w-full rounded-lg bg-muted" />
            <span className="form-facade-line mt-auto h-12 w-full rounded-xl bg-primary/80" />
          </button>
        )}
      </div>
    </div>
  );
};

export default FormEmbed;
